import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class ScoreCalculationService {
  constructor() {}

  // ⚠️ magic-constant remap, unvalidated — see results/OPEN_ISSUES.md OI-9
  calculateHormonalImprintFromPerfectCorp(perfectCorpAllScore: number): number {
    const rawScore = perfectCorpAllScore;
    const minInput = 72;
    const maxInput = 84;
    const minOutput = 7;
    const maxOutput = 94;

    let normalized =
      ((rawScore - minInput) / (maxInput - minInput)) *
        (maxOutput - minOutput) +
      minOutput;

    normalized += 7; // Add 7 to shift the scale up.

    if (normalized < minOutput) {
      normalized = minOutput;
    }
    if (normalized > maxOutput) {
      normalized = maxOutput;
    }

    let imprintScore = 100 - normalized;

    if (imprintScore < minOutput) {
      imprintScore = minOutput;
    }
    if (imprintScore > maxOutput) {
      imprintScore = maxOutput;
    }

    return Math.round(imprintScore);
  }

  // Step2 change3: the composite is the mean of these FOUR fixed scored tasks
  // ONLY. The two Stage-B bonus tasks (shape-sorter, vigilance-pulse) are still
  // shown as their own tiles with their own score, but never enter the average —
  // they run only for flagged users, tend to ceiling, and would otherwise both
  // inflate the flagged user's composite AND break longitudinal tracking (the
  // score jumped ~10pts purely on whether Stage B ran). recall-encode is unscored.
  // MIRRORED server-side — keep in sync: scan-cohort.controller.cognitiveComposite.
  static readonly COMPOSITE_TASKS = new Set([
    'reaction',
    'word-garden',
    'echo-chamber',
    'memory-scene',
  ]);

  calculateCompositeScore(cognitiveTests: any[]): number {
    if (!cognitiveTests || cognitiveTests.length === 0) {
      return 0;
    }

    const sharpV2Scores: number[] = [];

    cognitiveTests.forEach((test) => {
      if (!ScoreCalculationService.COMPOSITE_TASKS.has(test?.testId)) return;
      const s = this.calculateSharpV2TaskScore(test);
      if (s !== null) sharpV2Scores.push(s);
    });

    if (sharpV2Scores.length > 0) {
      const avg =
        sharpV2Scores.reduce((acc, v) => acc + v, 0) / sharpV2Scores.length;
      return Math.round(avg);
    }

    let totalAccuracy = 0;
    let testCount = 0;

    cognitiveTests.forEach((test) => {
      if (test.testId === 'delayed-recall' && test.response) {
        const wordsAcc = test.response.words?.accuracy || 0;
        const digitsAcc = test.response.digits?.accuracy || 0;
        totalAccuracy += wordsAcc + digitsAcc;
        testCount += 2;
        return;
      }

      let accuracy = 0;
      if (test.response?.accuracy !== undefined) {
        accuracy = test.response.accuracy;
      }
      totalAccuracy += accuracy;
      testCount++;
    });

    const averageAccuracy = testCount > 0 ? totalAccuracy / testCount : 0;
    return Math.round(averageAccuracy * 100);
  }

  // ⚠️ per-task formulas use magic constants, unvalidated — see results/OPEN_ISSUES.md OI-10
  calculateSharpV2TaskScore(test: any): number | null {
    if (!test || !test.response) return null;
    const r = test.response;
    switch (test.testId) {
      case 'reaction': {
        // Step2 change1 (medianPart anchors from product, step3 decision):
        // processing speed now weights the actual response-time median, not just
        // consistency. medianPart maps 350ms->100 .. 950ms->0 (linear), anchored
        // to a plausible human touch-screen tap range (NOT the tiny n=6 cohort),
        // so it holds as the cohort grows and never needs re-anchoring.
        // score = medianPart*0.50 + cvPart*0.35 + lapsePart*0.15.
        // MIRRORED server-side — keep in sync: scan-cohort.controller +
        // doctorReportViewService (both via sharp-v2-scoring util).
        const median = typeof r.median === 'number' ? r.median : 0;
        const cv = typeof r.cv === 'number' ? r.cv : 0;
        const lapses = typeof r.lapses === 'number' ? r.lapses : 0;
        // median<=0 means no scored hits at all (degenerate/all-miss) → no speed
        // signal, so score the speed part 0 rather than letting the formula credit
        // it 100 for the missing value.
        const medianPart =
          median > 0 ? this.clamp(100 - (median - 350) / 6, 0, 100) : 0;
        const cvPart = this.clamp(100 - (cv - 15) * 3, 0, 100);
        const lapsePart = this.clamp(100 - lapses * 12, 0, 100);
        return Math.round(medianPart * 0.5 + cvPart * 0.35 + lapsePart * 0.15);
      }
      case 'word-garden': {
        // Verbal-fluency band recalibrated to the REAL test distribution: the
        // prod cohort produces ~4-10 unique valid words (max 10, median ~7), so
        // the old 4->20 / 18->100 band needed 18 for a top score — unreachable
        // here, flooring every scan to ~20-54. Map 2->20 .. 12->100 (headroom
        // above the observed max of 10) so the real spread uses the full scale.
        // MIRRORED server-side — keep in sync: doctorReportViewService.sharpV2Score
        // + scan-cohort.controller.sharpV2TaskScore.
        const u = typeof r.uniqueValid === 'number' ? r.uniqueValid : 0;
        if (u <= 2) return 20;
        if (u >= 12) return 100;
        return Math.round(20 + ((u - 2) / 10) * 80);
      }
      case 'echo-chamber': {
        // Handoff 2026-08-17 (part C, Danna revision): digit span caps at forward
        // 6 / backward 5. Recalibrated so full marks are reachable at those caps
        // (denominators fwdMax-2=4 and bwdMax-1=4); otherwise the best echo score
        // would be below 100. MIRRORED server-side — keep in sync: sharp-v2-scoring.ts.
        const f = typeof r.maxForward === 'number' ? r.maxForward : 0;
        const b = typeof r.maxBackward === 'number' ? r.maxBackward : 0;
        const fPart = this.clamp(((f - 2) / 4) * 100, 0, 100);
        const bPart = this.clamp(((b - 1) / 4) * 100, 0, 100);
        return Math.round(fPart * 0.45 + bPart * 0.55);
      }
      case 'memory-scene': {
        // Step2 change2: score on FREE recall (weighted), not cued alone. The cued
        // phase is a 2-option forced choice (50% guess floor), so cuedScore alone
        // over-credits guessing. free 0.7 / cued 0.3. Delayed recall is the domain
        // the perimenopause literature (SWAN/Epperson) supports most, so it must be
        // scored on its most reliable signal. MIRRORED server-side — keep in sync:
        // doctorReportViewService.sharpV2Score + scan-cohort.controller.sharpV2TaskScore.
        const free = typeof r.freeScore === 'number' ? r.freeScore : 0;
        const cued = typeof r.cuedScore === 'number' ? r.cuedScore : 0;
        return Math.round(((free * 0.7 + cued * 0.3) / 4) * 100);
      }
      case 'shape-sorter':
        return typeof r.accuracy === 'number' ? Math.round(r.accuracy) : null;
      case 'vigilance-pulse': {
        const hr = typeof r.hitRate === 'number' ? r.hitRate : 0;
        const fa = typeof r.falseAlarms === 'number' ? r.falseAlarms : 0;
        return Math.round(this.clamp(hr - fa * 5, 0, 100));
      }
      default:
        return null;
    }
  }

  private clamp(v: number, lo: number, hi: number): number {
    return Math.max(lo, Math.min(hi, v));
  }

  // Symptom LOAD for the Markable Index. Unified 2026-08-17 to the shared
  // tier-saturation burden (same as the results/report/cohort), NOT mean×10: a
  // mean does not measure load (3 symptoms at 7 and 30 symptoms at 7 give the same
  // number). S = 3·pronounced(>=7) + 2·moderate(5-6) + 1·mild(1-4); soft ceiling 97;
  // K = 20·(nActive/60) so a 100-item scan doesn't inflate.
  calculateAverageBurden(symptoms: any, nActive = 60): number {
    if (!symptoms) return 0;

    const symptomValues = Object.values(symptoms).filter(
      (value): value is number => typeof value === 'number'
    );

    if (symptomValues.length === 0) return 0;

    const pronounced = symptomValues.filter((v) => v >= 7).length;
    const moderate = symptomValues.filter((v) => v > 4 && v < 7).length;
    const mild = symptomValues.filter((v) => v > 0 && v <= 4).length;
    const S = pronounced * 3 + moderate * 2 + mild;
    if (S <= 0) return 0;

    const K = 20 * ((nActive || 60) / 60);
    return Math.max(1, Math.round(97 * (1 - Math.exp(-S / K))));
  }

  calculateMarkableIndex(
    imprintScore: number,
    symptomBurden: number,
    sharpScore: number,
    initialSymptoms: any
  ): number {
    const cognitiveDifficulty = 100 - sharpScore;

    const rawIndex = (imprintScore + symptomBurden + cognitiveDifficulty) / 3;

    const symptomBoost = this.calculate9SymptomBoost(initialSymptoms);

    const adjustedIndex = rawIndex + symptomBoost;

    if (adjustedIndex < 20) return 20;
    if (adjustedIndex > 96) return 96;

    return Math.round(adjustedIndex);
  }

  calculate9SymptomBoost(initialSymptoms: any): number {
    if (!initialSymptoms) return 0;

    const nineSymptomKeys = [
      'heavy_flow',
      'night_sweats',
      'shorter_cycles',
      'breast_changes',
      'migraines',
      'weight_gain',
      'mood_swings',
      'night_waking',
      'increased_cramps',
    ];

    const yesCount = nineSymptomKeys.filter(
      (key) => initialSymptoms[key] === true
    ).length;

    if (yesCount === 0) return 0;
    if (yesCount === 1) return 5;
    if (yesCount === 2) return 10;
    if (yesCount === 3) return 16;
    if (yesCount === 4) return 18;
    if (yesCount >= 5) return 20;

    return 0;
  }
}
